import {
	getInitialPaymentSubStep,
	getPreviousDocumentsStep,
	getPreviousPaymentSubStep,
	getStatusConnectionPaymentMethods,
	isRegistrationStepVisible,
	RegistrationStep,
	requiresMyTaxConnectionFlow,
	combinesDocumentPhotosWithDetails,
	usesForeignDocumentDetailsForm,
	WORK_STATUS_STEP_ENABLED,
} from 'features/registration';
import type {RegistrationWizardState} from './useRegistrationWizardState';

type Params = {
	state: RegistrationWizardState;
	isEditingFromIdentityReview: boolean;
	cancelIdentityReviewEdit: () => void;
	handleClose: () => void;
};

/** Кнопка «Назад» в шапке: возврат по шагам регистрации, подключения статуса и подшагам оплаты. */
export const useRegistrationBackNavigation = ({
	state,
	isEditingFromIdentityReview,
	cancelIdentityReviewEdit,
	handleClose,
}: Params) => {
	const {
		documentsFlow,
		myTaxCheckingMode,
		myTaxLoadErrorRetry,
		myTaxNameMismatch,
		profileEntryStep,
		paymentSubStep,
		pendingPaymentMethod,
		personalInfo,
		registrationFlow,
		resetPaymentFlow,
		setPaymentSubStep,
		setPendingPaymentMethod,
		setStep,
		setWorkStatus,
		statusConnectionIncludesInn,
		statusConnectionIncludesSnils,
		step,
		workStatus,
	} = state;

	const includeDocumentPhotosStep =
		usesForeignDocumentDetailsForm(personalInfo.citizenshipOksmCode, personalInfo.residenceOksmCode) &&
		!combinesDocumentPhotosWithDetails(personalInfo.citizenshipOksmCode);

	/** Шаг, предшествующий подключению статуса: код резидента → СНИЛС → ИНН. */
	const goToPreviousStatusDocumentsStep = () => {
		if (isRegistrationStepVisible(registrationFlow.residentCode)) {
			setStep(RegistrationStep.ResidentCode);
			return;
		}

		if (statusConnectionIncludesSnils && isRegistrationStepVisible(registrationFlow.snils)) {
			setStep(RegistrationStep.Snils);
			return;
		}

		if (statusConnectionIncludesInn) {
			setStep(RegistrationStep.Inn);
			return;
		}

		setWorkStatus(null);

		if (WORK_STATUS_STEP_ENABLED) {
			setStep(RegistrationStep.WorkStatus);
		} else {
			handleClose();
		}
	};

	return () => {
		if (isEditingFromIdentityReview) {
			cancelIdentityReviewEdit();
			return;
		}

		if (profileEntryStep !== null && step === profileEntryStep) {
			handleClose();
			return;
		}

		if (step === RegistrationStep.MyTaxFnsPending) {
			setStep(RegistrationStep.MyTaxChecking);
			return;
		}

		if (step === RegistrationStep.PartnerAgreementPending) {
			if (WORK_STATUS_STEP_ENABLED) {
				setStep(RegistrationStep.WorkStatus);
			} else {
				handleClose();
			}
			return;
		}

		if (step === RegistrationStep.MyTaxAccessError) {
			setStep(RegistrationStep.MyTaxAccess);
			return;
		}

		if (step === RegistrationStep.MyTaxNotSelfEmployed) {
			setStep(RegistrationStep.MyTaxChecking);
			return;
		}

		if (step === RegistrationStep.MyTaxLoadError) {
			if (myTaxLoadErrorRetry === 'partner_agreement') {
				setStep(RegistrationStep.MyTaxIntro);
				return;
			}

			if (myTaxLoadErrorRetry === 'activate' && myTaxNameMismatch) {
				setStep(RegistrationStep.MyTaxNameMismatch);
				return;
			}

			setStep(RegistrationStep.MyTaxChecking);
			return;
		}

		if (step === RegistrationStep.MyTaxNameMismatch) {
			setStep(RegistrationStep.MyTaxChecking);
			return;
		}

		if (step === RegistrationStep.MyTaxSuccess) {
			if (requiresMyTaxConnectionFlow(workStatus)) {
				setStep(RegistrationStep.MyTaxChecking);
				return;
			}

			const paymentMethods = getStatusConnectionPaymentMethods(workStatus, registrationFlow.paymentMethods);

			if (paymentMethods.length > 0) {
				setPendingPaymentMethod(paymentMethods[0]);
				setPaymentSubStep(getInitialPaymentSubStep(paymentMethods[0]));
				setStep(RegistrationStep.PaymentMethod);
				return;
			}

			goToPreviousStatusDocumentsStep();
			return;
		}

		if (step === RegistrationStep.PartnerAgreementChecking) {
			setStep(RegistrationStep.MyTaxIntro);
			return;
		}

		if (step === RegistrationStep.MyTaxChecking) {
			if (myTaxCheckingMode === 'permissions') {
				setStep(RegistrationStep.MyTaxAccess);
				return;
			}

			goToPreviousStatusDocumentsStep();
			return;
		}

		if (step === RegistrationStep.MyTaxAccess) {
			goToPreviousStatusDocumentsStep();
			return;
		}

		if (step === RegistrationStep.MyTaxIntro) {
			setWorkStatus(null);
			if (WORK_STATUS_STEP_ENABLED) {
				setStep(RegistrationStep.WorkStatus);
			} else {
				handleClose();
			}
			return;
		}

		if (step === RegistrationStep.Inn) {
			setStep(RegistrationStep.MyTaxIntro);
			return;
		}

		if (step === RegistrationStep.ResidentCode) {
			if (statusConnectionIncludesSnils && isRegistrationStepVisible(registrationFlow.snils)) {
				setStep(RegistrationStep.Snils);
				return;
			}

			if (statusConnectionIncludesInn) {
				setStep(RegistrationStep.Inn);
				return;
			}

			setWorkStatus(null);

			if (WORK_STATUS_STEP_ENABLED) {
				setStep(RegistrationStep.WorkStatus);
			} else {
				handleClose();
			}

			return;
		}

		if (step === RegistrationStep.Snils) {
			if (statusConnectionIncludesInn) {
				setStep(RegistrationStep.Inn);
				return;
			}

			setWorkStatus(null);

			if (WORK_STATUS_STEP_ENABLED) {
				setStep(RegistrationStep.WorkStatus);
			} else {
				handleClose();
			}

			return;
		}

		if (step === RegistrationStep.IdentityReview) {
			setStep(
				documentsFlow.residence ? RegistrationStep.ResidenceDocument : RegistrationStep.IdentificationInn,
			);
			return;
		}

		if (step === RegistrationStep.ResidenceDocument) {
			setStep(RegistrationStep.IdentificationInn);
			return;
		}

		if (step === RegistrationStep.IdentificationInn) {
			setStep(RegistrationStep.DocumentDetails);
			return;
		}

		if (step === RegistrationStep.DocumentDetails) {
			setStep(getPreviousDocumentsStep(step, documentsFlow, includeDocumentPhotosStep));
			return;
		}

		if (step === RegistrationStep.DocumentPhotos) {
			setStep(RegistrationStep.PersonalInfo);
			return;
		}

		if (step === RegistrationStep.PersonalInfo || step === RegistrationStep.Citizenship) {
			handleClose();
			return;
		}

		if (WORK_STATUS_STEP_ENABLED && step === RegistrationStep.WorkStatus) {
			setStep(RegistrationStep.IdentityReview);
			return;
		}

		if (step === RegistrationStep.PaymentMethod && !pendingPaymentMethod) {
			goToPreviousStatusDocumentsStep();
			return;
		}

		if (step === RegistrationStep.PaymentMethod && pendingPaymentMethod) {
			const previousSubStep = getPreviousPaymentSubStep(pendingPaymentMethod, paymentSubStep);
			const singlePaymentMethod =
				getStatusConnectionPaymentMethods(workStatus, registrationFlow.paymentMethods).length === 1;

			// Один способ (ИП) — экрана выбора нет: с первого подшага уходим на предыдущий шаг регистрации.
			if (previousSubStep === 'select' && singlePaymentMethod) {
				resetPaymentFlow();
				goToPreviousStatusDocumentsStep();
				return;
			}

			if (previousSubStep) {
				setPaymentSubStep(previousSubStep);
				return;
			}

			setPendingPaymentMethod(null);
			setPaymentSubStep('select');
			return;
		}

		if (step > RegistrationStep.PersonalInfo) {
			setStep((current) => current - 1);
			return;
		}

		handleClose();
	};
};
